/**
 * Widget height (FB-012).
 *
 * Width was already adjustable through `span`; height was not modelled at all,
 * so a table could not be made tall enough to show more rows and a trend chart
 * could not be enlarged to make a pattern readable. Both were the request.
 *
 * Height is expressed in the same units as width — 1 to 3 — rather than pixels,
 * so a dashboard keeps its proportions across breakpoints and a saved widget
 * cannot end up taller than the viewport on a laptop.
 *
 * A widget with no `rowSpan` renders exactly as before: the base values are the
 * heights that were previously hardcoded, so existing dashboards are untouched.
 */
export type RowSpan = 1 | 2 | 3;

/** Previously hardcoded on the widget body. */
const BODY_BASE_PX = 120;
/** Previously hardcoded on the chart canvas. */
const CHART_BASE_PX = 140;
/** Geo/map canvas, which shipped slightly taller. */
const MAP_BASE_PX = 160;
/** Added per extra row. Enough to be worth choosing, not enough to fill a screen. */
const STEP_PX = 180;

export const clampRowSpan = (rowSpan: number | undefined): RowSpan => {
  if (!rowSpan || Number.isNaN(rowSpan)) return 1;
  return Math.min(3, Math.max(1, Math.round(rowSpan))) as RowSpan;
};

const scale = (base: number, rowSpan: number | undefined): number => base + STEP_PX * (clampRowSpan(rowSpan) - 1);

/**
 * Row span a vertical drag resolves to.
 *
 * Extracted from the resize handler so the clamping is testable: the handler
 * only had DOM-driven coverage, and dragging up past the top must land on 1
 * rather than 0 — a zero-height widget breaks the grid it sits in.
 *
 * `rectHeight` is the widget's current pixel height. A zero or missing height
 * (a widget measured before layout) would make the per-row divisor 0 and the
 * result NaN, so it holds the current span instead of jumping.
 */
export const nextRowSpanFromDrag = (
  currentRowSpan: number | undefined,
  rectHeight: number,
  deltaY: number,
): RowSpan => {
  const current = clampRowSpan(currentRowSpan);
  const rowHeight = rectHeight / current;
  if (!Number.isFinite(rowHeight) || rowHeight <= 0) return current;
  return clampRowSpan(current + deltaY / rowHeight);
};

/** Minimum height of the widget body. */
export const widgetBodyMinHeight = (rowSpan?: number): number => scale(BODY_BASE_PX, rowSpan);

/** Height of a chart canvas inside the body. */
export const widgetChartHeight = (rowSpan?: number): number => scale(CHART_BASE_PX, rowSpan);

/** Height of a map canvas inside the body. */
export const widgetMapHeight = (rowSpan?: number): number => scale(MAP_BASE_PX, rowSpan);

/** Options for the Height picker, labelled like the Width picker. */
export const ROW_SPAN_OPTIONS: { value: RowSpan; label: string }[] = [
  { value: 1, label: 'Standard' },
  { value: 2, label: 'Tall' },
  { value: 3, label: 'Extra tall' },
];
